import React, { useRef } from 'react';
import { useMonsterManagement } from '../../hooks/useMonsterManagement';
import { useScaleHover } from '../../hooks/useScaleHover';
import { ATTRIBUTES, ATTR_IMG, STAR_IMG } from '../../constants/monster';
import MonsterSelect from './MonsterSelect';
import MonsterImage from '../../components/MonsterImage';
import MonsterStats from '../../components/MonsterStats';
import { hiddenByCollab } from '../../utils/monster';
import { loadMonsterMapping } from '../../utils/monsterMapping';
import { useBackHandler } from '../../hooks/useBackHandler';

interface MonsterProps {
  monsterMapping: any;
  setMonsterMapping: (data: any) => void;
}



const ATTRIBUTE_KEYS = ATTRIBUTES.map(a => a.key);
const awakeStates = ['Unawaken', 'Awakened', 'SecondAwakened'] as const;

const Monster: React.FC<MonsterProps> = (props) => {
  const monsterMgmt = useMonsterManagement(props.monsterMapping, props.setMonsterMapping);

  const monsterRefs = useRef<{ [key: string]: HTMLDivElement | null }>({});

  const aliasAddBtnScale = useScaleHover<HTMLButtonElement>();

  // 뒤로 가기: 콜라보몹 고르기 → 몬스터 관리 팝업(바깥 누름과 같이 닫기만)
  useBackHandler(!!monsterMgmt.managePopup, () => monsterMgmt.setManagePopup(null));
  useBackHandler(monsterMgmt.showCollabSelect, () => monsterMgmt.setShowCollabSelect(false));

  return (
    <div 
      style={{ 
        height: '100vh',
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        padding: '10px 8px',
        position: 'relative',
        overflowY: 'auto',
        overflowX: 'hidden',
        scrollbarWidth: 'thin',
        scrollbarColor: 'rgba(255, 255, 255, 0.5) transparent'
      }}
      className="monster-page-scroll"
    >
      {/* 배경 장식 요소 */}
      <div style={{
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'radial-gradient(circle at 20% 50%, rgba(255,255,255,0.1) 0%, transparent 50%), radial-gradient(circle at 80% 20%, rgba(255,255,255,0.1) 0%, transparent 50%), radial-gradient(circle at 40% 80%, rgba(255,255,255,0.1) 0%, transparent 50%)',
        pointerEvents: 'none'
      }} />
      
      <div style={{ 
        maxWidth: '100%', 
        margin: '0 auto', 
        position: 'relative',
        zIndex: 1
      }}>
        {/* 헤더 */}
        <div style={{
          background: 'rgba(255, 255, 255, 0.95)',
          borderRadius: 16,
          padding: '16px 20px',
          marginBottom: 16,
          boxShadow: '0 4px 16px rgba(0, 0, 0, 0.1)',
          backdropFilter: 'blur(20px)',
          border: '1px solid rgba(255, 255, 255, 0.2)'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, marginBottom: 12 }}>
            <h1 style={{
              fontWeight: 800,
              fontSize: 24,
              margin: 0,
              textAlign: 'center',
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text'
            }}>몬스터 관리</h1>
          </div>
          <p style={{ 
            color: '#666', 
            fontSize: 14, 
            margin: 0,
            fontWeight: 500,
            textAlign: 'center'
          }}>몬스터 관리 페이지입니다.</p>
        </div>

        {/* 컨트롤 패널 */}
        <div style={{
          background: 'rgba(255, 255, 255, 0.95)',
          borderRadius: 16,
          padding: '16px 20px',
          marginBottom: 16,
          boxShadow: '0 4px 16px rgba(0, 0, 0, 0.1)',
          backdropFilter: 'blur(20px)',
          border: '1px solid rgba(255, 255, 255, 0.2)'
        }}>
          {/* 별 선택 */}
          <div style={{ marginBottom: 16 }}>
            <h3 style={{ 
              color: '#333', 
              fontSize: 14, 
              fontWeight: 700, 
              marginBottom: 12,
              display: 'flex',
              alignItems: 'center',
              gap: 2
            }}>
              태성
            </h3>
            <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
              {[1,2,3,4,5].map(star => (
                <button
                  key={star}
                  onClick={() => monsterMgmt.setAdminStar(star)}
                  style={{
                    background: monsterMgmt.adminStar === star 
                      ? 'linear-gradient(135deg, #ffd600 0%, #ff8f00 100%)' 
                      : 'rgba(255, 255, 255, 0.8)',
                    border: monsterMgmt.adminStar === star ? '2px solid #ffd600' : '2px solid #e0e0e0',
                    borderRadius: 6,
                    padding: '6px 5px',
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: 0,
                    cursor: 'pointer',
                    boxShadow: monsterMgmt.adminStar === star 
                      ? '0 4px 20px rgba(255, 214, 0, 0.4)' 
                      : '0 2px 8px rgba(0, 0, 0, 0.1)',
                    outline: 'none',
                    transition: 'all 0.3s ease',
                    transform: monsterMgmt.adminStar === star ? 'translateY(-2px)' : 'translateY(0)'
                  }}
                  onMouseEnter={(e) => {
                    if (monsterMgmt.adminStar !== star) {
                      e.currentTarget.style.transform = 'translateY(-2px)';
                      e.currentTarget.style.boxShadow = '0 4px 16px rgba(0, 0, 0, 0.15)';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (monsterMgmt.adminStar !== star) {
                      e.currentTarget.style.transform = 'translateY(0)';
                      e.currentTarget.style.boxShadow = '0 2px 8px rgba(0, 0, 0, 0.1)';
                    }
                  }}
                >
                  {Array.from({length: star}).map((_,i) => (
                    <img loading="lazy" key={i} src={STAR_IMG[1]} alt="*" style={{ width: 14, height: 14 }} />
                  ))}
                </button>
              ))}
            </div>
          </div>

          {/* 속성 선택 */}
          <div style={{ marginBottom: 16 }}>
            <h3 style={{ 
              color: '#333', 
              fontSize: 16, 
              fontWeight: 700, 
              marginBottom: 12,
              display: 'flex',
              alignItems: 'center',
              gap: 6
            }}>
              속성
            </h3>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              {ATTRIBUTES.map(attr => (
                <button
                  key={attr.key}
                  onClick={() => monsterMgmt.setAdminAttr(attr.key)}
                  style={{
                    background: monsterMgmt.adminAttr === attr.key 
                      ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' 
                      : 'rgba(255, 255, 255, 0.8)',
                    border: monsterMgmt.adminAttr === attr.key ? '2px solid #667eea' : '2px solid #e0e0e0',
                    borderRadius: 8,
                    padding: '8px 12px',
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'center',
                    cursor: 'pointer',
                    boxShadow: monsterMgmt.adminAttr === attr.key 
                      ? '0 4px 20px rgba(102, 126, 234, 0.4)' 
                      : '0 2px 8px rgba(0, 0, 0, 0.1)',
                    outline: 'none',
                    transition: 'all 0.3s ease',
                    transform: monsterMgmt.adminAttr === attr.key ? 'translateY(-2px)' : 'translateY(0)',
                    position: 'relative'
                  }}
                  onMouseEnter={(e) => {
                    if (monsterMgmt.adminAttr !== attr.key) {
                      e.currentTarget.style.transform = 'translateY(-2px)';
                      e.currentTarget.style.boxShadow = '0 4px 16px rgba(0, 0, 0, 0.15)';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (monsterMgmt.adminAttr !== attr.key) {
                      e.currentTarget.style.transform = 'translateY(0)';
                      e.currentTarget.style.boxShadow = '0 2px 8px rgba(0, 0, 0, 0.1)';
                    }
                  }}
                  title={attr.label}
                >
                  <img loading="lazy" src={ATTR_IMG[attr.key]} alt={attr.label} style={{ width: 14, height: 14 }} />
                  {monsterMgmt.adminAttr === attr.key && (
                    <div style={{
                      position: 'absolute',
                      top: -4,
                      right: -4,
                      width: 20,
                      height: 20,
                      background: '#4caf50',
                      borderRadius: '50%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: 12,
                      color: '#fff'
                    }}>
                      ✓
                    </div>
                  )}
                </button>
              ))}
            </div>
          </div>

          {/* 검색창 */}
          <div>
            <h3 style={{ 
              color: '#333', 
              fontSize: 16, 
              fontWeight: 700, 
              marginBottom: 12,
              display: 'flex',
              alignItems: 'center',
              gap: 6
            }}>
              몬스터 검색
            </h3>
            <div style={{ position: 'relative', maxWidth: '100%' }}>
              <input
                type="text"
                value={monsterMgmt.search}
                onChange={e => monsterMgmt.setSearch(e.target.value)}
                placeholder="몬스터 이름 또는 별명으로 검색..."
                style={{
                  width: '100%',
                  padding: '12px 16px 12px 40px',
                  border: '1px solid #e0e0e0',
                  borderRadius: 12,
                  fontSize: 14,
                  background: 'rgba(255, 255, 255, 0.9)',
                  outline: 'none',
                  transition: 'all 0.3s ease',
                  boxShadow: '0 1px 4px rgba(0, 0, 0, 0.1)'
                }}
                onFocus={(e) => {
                  e.currentTarget.style.borderColor = '#667eea';
                  e.currentTarget.style.boxShadow = '0 2px 12px rgba(102, 126, 234, 0.2)';
                }}
                onBlur={(e) => {
                  e.currentTarget.style.borderColor = '#e0e0e0';
                  e.currentTarget.style.boxShadow = '0 1px 4px rgba(0, 0, 0, 0.1)';
                }}
              />
            </div>
          </div>
        </div>

        {/* 몬스터 그리드 */}
        <div style={{
          background: 'rgba(255, 255, 255, 0.95)',
          borderRadius: 10,
          padding: '16px',
          boxShadow: '0 4px 16px rgba(0, 0, 0, 0.1)',
          backdropFilter: 'blur(20px)',
          border: '1px solid rgba(255, 255, 255, 0.2)',
          marginBottom: 16
        }}>
          <div style={{ 
            display: 'grid', 
            gridTemplateColumns: 'repeat(auto-fill, minmax(65px, 1fr))', 
            gap: 8, 
            justifyContent: 'center' 
          }}>
            {props.monsterMapping?.monsters && (() => {
              const allMonsters = Object.entries(props.monsterMapping.monsters);
              const lowerSearch = monsterMgmt.search.trim().toLowerCase();
              let filtered: [string, any, string, string | null][] = [];
              if (lowerSearch) {
                allMonsters.forEach(([name, info]: any) => {
                  Object.entries(info).forEach(([attribute, v]: any) => {
                    if (!ATTRIBUTE_KEYS.includes(attribute)) return;
                    const aliases = v?.aliases || [];
                    let imgPath: string | null = null;
                    if (v?.SecondAwakened && v.SecondAwakened.filepath) {
                      imgPath = v.SecondAwakened.filepath;
                    } else if (v?.Awakened && v.Awakened.filepath) {
                      imgPath = v.Awakened.filepath;
                    } else if (v?.Unawaken && v.Unawaken.filepath) {
                      imgPath = v.Unawaken.filepath;
                    }
                    if (
                      name.toLowerCase().includes(lowerSearch) ||
                      aliases.some((alias: string) => alias.toLowerCase().includes(lowerSearch))
                    ) {
                      filtered.push([name, info, attribute, imgPath]);
                    }
                  });
                });
                filtered = filtered.filter((item, idx, arr) =>
                  arr.findIndex(([n, , a]) => n === item[0] && a === item[2]) === idx
                );
              } else {
                filtered = allMonsters
                  .filter(([name, info]: any) => info[monsterMgmt.adminAttr]?.Awakened && info[monsterMgmt.adminAttr].Awakened.stars === monsterMgmt.adminStar)
                  .map(([name, info]: any) => {
                    let imgPath: string | null = null;
                    if (info[monsterMgmt.adminAttr]?.SecondAwakened && info[monsterMgmt.adminAttr].SecondAwakened.stars === monsterMgmt.adminStar && info[monsterMgmt.adminAttr].SecondAwakened.filepath) {
                      imgPath = info[monsterMgmt.adminAttr].SecondAwakened.filepath;
                    } else if (info[monsterMgmt.adminAttr]?.Awakened && info[monsterMgmt.adminAttr].Awakened.stars === monsterMgmt.adminStar && info[monsterMgmt.adminAttr].Awakened.filepath) {
                      imgPath = info[monsterMgmt.adminAttr].Awakened.filepath;
                    } else if (info[monsterMgmt.adminAttr]?.Unawaken && info[monsterMgmt.adminAttr].Unawaken.stars === monsterMgmt.adminStar && info[monsterMgmt.adminAttr].Unawaken.filepath) {
                      imgPath = info[monsterMgmt.adminAttr].Unawaken.filepath;
                    }
                    return [name, info, monsterMgmt.adminAttr, imgPath];
                  });
              }
                        return filtered
                          .filter(([name, info]: any) => !hiddenByCollab(props.monsterMapping, name, info))
                          .map(([name, info, attribute, imgPath]) => (
            <div
              key={name + attribute}
              ref={el => { monsterRefs.current[name + attribute] = el; }}
              onClick={() => {
                // 대표 각성 상태 우선순위: SecondAwakened > Awakened > Unawaken
                const monsterInfo = info[attribute];
                let stars = monsterInfo.Unawaken.stars;
                monsterMgmt.setAdminAttr(attribute);
                monsterMgmt.setAdminStar(stars);
                monsterMgmt.setManagePopup({ name, info, attribute });
              }}
              style={{
                background: 'rgba(255, 255, 255, 0.9)',
                borderRadius: 8,
                padding: 6,
                cursor: 'pointer',
                transition: 'all 0.3s ease',
                border: '1px solid rgba(255, 255, 255, 0.3)',
                boxShadow: '0 1px 8px rgba(0, 0, 0, 0.1)',
                backdropFilter: 'blur(10px)',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: 4,
                position: 'relative',
                overflow: 'hidden'
              }}
              onTouchStart={(e) => {
                e.currentTarget.style.transform = 'translateY(-2px) scale(1.01)';
                e.currentTarget.style.boxShadow = '0 4px 16px rgba(0, 0, 0, 0.15)';
                e.currentTarget.style.borderColor = 'rgba(102, 126, 234, 0.5)';
              }}
              onTouchEnd={(e) => {
                e.currentTarget.style.transform = 'translateY(0) scale(1)';
                e.currentTarget.style.boxShadow = '0 2px 12px rgba(0, 0, 0, 0.1)';
                e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.3)';
              }}
              title={name}
            >
              {/* 속성 아이콘 */}
              <div style={{
                position: 'absolute',
                top: 4,
                right: 4,
                width: 14,
                height: 14,
                borderRadius: '50%',
                background: 'rgba(255, 255, 255, 0.9)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)'
              }}>
                <img loading="lazy" src={ATTR_IMG[attribute]} alt={attribute} style={{ width: 10, height: 10 }} />
              </div>
              
              {/* 몬스터 이미지 */}
              <div style={{
                width: 45,
                height: 45,
                borderRadius: 6,
                background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                overflow: 'hidden',
                border: '1px solid rgba(255, 255, 255, 0.8)',
                boxShadow: '0 1px 4px rgba(0, 0, 0, 0.1)'
              }}>
                <MonsterImage
                  imgPath={imgPath}
                  alt={name}
                  style={{ 
                    width: '100%', 
                    height: '100%', 
                    objectFit: 'contain',
                    transition: 'transform 0.3s ease'
                  }}
                />
              </div>
              
              {/* 몬스터 이름 */}
              <div style={{ 
                fontSize: 8, 
                fontWeight: 600,
                color: '#333', 
                textAlign: 'center', 
                maxWidth: '100%',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                lineHeight: 1.1
              }}>
                {monsterMgmt.getDisplayName(name, attribute)}
              </div>
            </div>
          ));
            })()}
          </div>
        </div>
      </div>
      {/* 관리탭 UI/팝업/기능 모두 이곳에 구현 (팝업 포함) */}
      {/* 몬스터 관리 팝업 */}
      {monsterMgmt.managePopup && (
        <div
          className="modal-backdrop"
          style={{ position: 'fixed', left: 0, top: 0, width: '100vw', height: '100vh', background: 'rgba(30,40,60,0.18)', zIndex: 200, display: 'flex', alignItems: 'center', justifyContent: 'center', backdropFilter: 'blur(2px)' }}
          onClick={() => monsterMgmt.setManagePopup(null)}
        >
          <div
            className="modal"
            style={{ background: 'linear-gradient(135deg, #fafdff 60%, #e3f0ff 100%)', borderRadius: 16, maxWidth: 360, width: '95vw', padding: 20, boxShadow: '0 4px 24px 0 #1976d244, 0 1px 4px #0001', position: 'relative', display: 'flex', flexDirection: 'column', alignItems: 'center', border: '1px solid #e3f0ff' }}
            onClick={e => e.stopPropagation()}
          >
            <button
              onClick={async () => {
                monsterMgmt.setManagePopup(null);
                monsterMgmt.setMobImgVersion(Date.now());
                try {
                  const mappingData = await loadMonsterMapping();
                  props.setMonsterMapping(mappingData);
                } catch (e) {
                  // 무시
                }
              }}
              style={{ position: 'absolute', top: 12, right: 12, background: 'none', border: 'none', fontSize: 20, color: 'var(--text-main)', cursor: 'pointer', fontWeight: 700, transition: 'color 0.2s' }}
              onMouseOver={e => e.currentTarget.style.color='#1976d2'}
              onMouseOut={e => e.currentTarget.style.color='var(--text-main)'}
            >×</button>
            
            {/* 속성 이동 버튼 */}
            <div style={{ display: 'flex', gap: 6, marginTop: 0, justifyContent: 'center' }}>
              {ATTRIBUTES.map(attr => {
                const hasAttr = !!monsterMgmt.managePopup!.info[attr.key];
                const isCurrent = monsterMgmt.managePopup!.attribute === attr.key;
                return (
                  <button
                    key={attr.key}
                    disabled={!hasAttr}
                    onClick={() => {
                      if (hasAttr && !isCurrent) monsterMgmt.setManagePopup({ name: monsterMgmt.managePopup!.name, info: monsterMgmt.managePopup!.info, attribute: attr.key });
                    }}
                    style={{
                      background: isCurrent ? '#1976d2' : 'var(--bg-card)',
                      border: isCurrent ? '2px solid #1976d2' : '2px solid #e3f0ff',
                      borderRadius: '50%',
                      width: 32, height: 32,
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      cursor: hasAttr && !isCurrent ? 'pointer' : 'not-allowed',
                      opacity: hasAttr ? 1 : 0.4,
                      outline: 'none',
                      padding: 0,
                      boxShadow: isCurrent ? '0 2px 8px #1976d255' : 'none',
                      marginRight: 2
                    }}
                  >
                    <img loading="lazy" src={ATTR_IMG[attr.key]} alt={attr.label} style={{ width: 20, height: 20, filter: isCurrent ? 'none' : 'grayscale(40%)', opacity: hasAttr ? 1 : 0.5 }} />
                  </button>
                );
              })}
            </div>
            {/* 각성상태 전환 버튼 - pill형 탭 스타일 */}
            <div style={{ display: 'flex', alignItems: 'center', marginBottom: 16, marginTop: 0, background: 'var(--bg-card)', borderRadius: 999, padding: 3, gap: 1, boxShadow: '0 1px 4px #1976d211' }}>
              {awakeStates.map(state => (
                <button
                  key={state}
                  onClick={() => monsterMgmt.handleAwakeStateChange(state as 'Unawaken'|'Awakened'|'SecondAwakened')}
                  style={{
                    background: monsterMgmt.manageAwakeState === state ? '#1976d2' : 'transparent',
                    color: monsterMgmt.manageAwakeState === state ? '#fff' : 'var(--text-main)',
                    border: '2px solid #1976d2',
                    borderRadius: 999,
                    fontWeight: 700,
                    fontSize: 12,
                    padding: '4px 16px',
                    cursor: 'pointer',
                    transition: 'background 0.18s, color 0.18s',
                    boxShadow: monsterMgmt.manageAwakeState === state ? '0 2px 8px #1976d233' : 'none',
                  }}
                >
                  {state === 'Unawaken' ? '미각성' : state === 'Awakened' ? '각성' : '2차각성'}
                </button>
              ))}
            </div>
            <div style={{ width: '100%', display: 'flex', justifyContent: 'center', alignItems: 'center', marginBottom: 6, minHeight: 80, position: 'relative' }}>
              {(() => {
                let info = monsterMgmt.managePopup.info;
                if (!info) {
                  info = {
                    [monsterMgmt.managePopup.attribute]: {
                      Awakened: {
                        aliases: [],
                        stars: monsterMgmt.adminStar,
                        filepath: undefined
                      }
                    }
                  };
                }
                let imgPath = null;
                if (
                  monsterMgmt &&
                  monsterMgmt.managePopup &&
                  monsterMgmt.managePopup.info &&
                  monsterMgmt.managePopup.info[monsterMgmt.managePopup.attribute]?.[monsterMgmt.manageAwakeState]
                ) {
                  imgPath = monsterMgmt.managePopup.info[monsterMgmt.managePopup.attribute][monsterMgmt.manageAwakeState].filepath;
                }
                if (imgPath) {
                  return (
                    <div
                     
                      style={{ position: 'relative', width: 120, height: 120, marginBottom: 18, boxShadow: '0 4px 24px #1976d222, 0 1.5px 8px #0001', borderRadius: 16, background: 'var(--bg-card)' }}
                    >
                      <img loading="lazy" src={`/mobs/v2/${imgPath.replace(/\\/g, '/')}?raw=1&v=${monsterMgmt.mobImgVersion}`} alt={monsterMgmt.managePopup.name} style={{ width: 120, height: 120, objectFit: 'contain', borderRadius: 16, background: '#fff' }} />
                    </div>
                  );
                } else {
                  // 그림이 없으면 그렇다고만 알린다. 올리는 기능은 걷어냈다 —
                  // 몹 그림은 [몬스터 업데이트] 가 SWARFARM 에서 받아 채운다.
                  return <div style={{ width: 120, height: 120, background: 'var(--bg-card)', borderRadius: 16, marginBottom: 18, display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', boxShadow: '0 4px 24px #1976d211', border: '2px dashed #b0bec5', outline: 'none', color: 'var(--text-sub, #90a4ae)', fontSize: 12, textAlign: 'center', padding: 8 }}>
                    그림 없음
                  </div>;
                }
              })()}
              {/* 콜라보몹이 있으면 이미지, 없으면 +버튼 */}
              {monsterMgmt.managePopup?.info?.collab ? (
                (() => {
                  const collabName = monsterMgmt.managePopup.info.collab;
                  let collabImg = '';
                  if (props.monsterMapping?.monsters?.[collabName]) {
                    const info = props.monsterMapping.monsters[collabName];
                    if (info[monsterMgmt.managePopup.attribute]?.Awakened?.filepath) {
                      // 관리 팝업은 원본·콜라보를 각각 보여주는 자리라 합친 그림이면 안 된다
                      collabImg = `/mobs/v2/${info[monsterMgmt.managePopup.attribute].Awakened.filepath.replace(/\\/g, '/')}?raw=1`;
                    }
                    else if (info[monsterMgmt.managePopup.attribute]?.Unawaken?.filepath) {
                      collabImg = `/mobs/v2/${info[monsterMgmt.managePopup.attribute].Unawaken.filepath.replace(/\\/g, '/')}?raw=1`;
                    }
                  }
                  return (
                    <div
                      style={{
                        position: 'absolute',
                        bottom: 16,
                        right: 50,
                        width: 48,
                        height: 48,
                        border: '2px solid #1976d2',
                        borderRadius: '20%',
                        background: '#fff',
                        boxShadow: '0 2px 8px #0002',
                        overflow: 'visible',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                      onMouseEnter={e => {
                        const xBtn = e.currentTarget.querySelector('.collab-x-btn');
                        if (xBtn) (xBtn as HTMLElement).style.opacity = '1';
                      }}
                      onMouseLeave={e => {
                        const xBtn = e.currentTarget.querySelector('.collab-x-btn');
                        if (xBtn) (xBtn as HTMLElement).style.opacity = '0';
                      }}
                    >
                      <img loading="lazy"
                        src={collabImg || '/images/empty.png'}
                        alt={collabName}
                        style={{
                          width: '100%',
                          height: '100%',
                          borderRadius: '20%',
                          objectFit: 'cover',
                          display: 'block',
                          background: '#fff',
                        }}
                      />
                      <span
                        className="collab-x-btn"
                        style={{
                          position: 'absolute',
                          top: 2,
                          right: 2,
                          width: 22,
                          height: 22,
                          background: 'transparent',
                          borderRadius: '50%',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          color: '#d32f2f',
                          fontWeight: 700,
                          fontSize: 18,
                          boxShadow: '0 1px 4px #0001',
                          cursor: 'pointer',
                          opacity: 0,
                          transition: 'opacity 0.18s',
                          zIndex: 2,
                        }}
                        onClick={monsterMgmt.handleRemoveCollab}
                        onMouseEnter={e => (e.currentTarget.style.background = 'transparent')}
                        onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}
                        title="연동 해제"
                      >×</span>
                      {/*
                        어느 쪽이 콜라보인지 정하는 버튼.

                        목록은 콜라보 쪽 이름으로 한 칸만 그린다(원본까지 그리면 같은 몹이
                        두 개로 보인다). 그 방향은 데이터로 알 수 없다 — SWARFARM API 에
                        콜라보 정보가 없고, 스킬 그룹이 같다는 것만으로는 엘리트 변종과
                        구별되지 않는다. 그래서 사람이 누른다.
                      */}
                      <span
                        style={{
                          position: 'absolute',
                          bottom: -10,
                          left: '50%',
                          transform: 'translateX(-50%)',
                          whiteSpace: 'nowrap',
                          padding: '2px 8px',
                          borderRadius: 10,
                          fontSize: 11,
                          fontWeight: 700,
                          cursor: monsterMgmt.managePopup?.info?.isCollab ? 'default' : 'pointer',
                          background: monsterMgmt.managePopup?.info?.isCollab ? '#1976d2' : '#fff',
                          color: monsterMgmt.managePopup?.info?.isCollab ? '#fff' : '#1976d2',
                          border: '1.5px solid #1976d2',
                          boxShadow: '0 1px 4px #0002',
                          zIndex: 3,
                        }}
                        title={monsterMgmt.managePopup?.info?.isCollab
                          ? `이 몹이 콜라보입니다. 누르면 ${collabName} 쪽으로 바뀝니다`
                          : '이 몹을 콜라보로 지정합니다 (목록에서 짝은 감춰집니다)'}
                        onClick={(e) => {
                          e.stopPropagation();
                          // 누를 때마다 서로 바뀐다. 이미 이 몹이 콜라보면 짝을 콜라보로 만든다.
                          const mine = monsterMgmt.managePopup?.info?.isCollab;
                          monsterMgmt.handleMarkCollabSide(mine ? collabName : undefined);
                        }}
                      >콜라보</span>
                    </div>
                  );
                })()
              ) : (
                <button
                  style={{
                    position: 'absolute',
                    bottom: 16,
                    right: 50,
                    width: 48,
                    height: 48,
                    border: '2px dashed #1976d2',
                    marginBottom: 8,
                    color: '#1976d2', fontSize: 32, fontWeight: 700,
                    transition: 'box-shadow 0.2s, border 0.2s',
                    borderRadius: 6,
                    background: '#fff',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    cursor: 'pointer',
                    padding: 0,
                    boxShadow: '0 2px 8px #0002'
                  }}
                  title="콜라보 몹 연동"
                  onClick={() => monsterMgmt.setShowCollabSelect(true)}
                >+
                </button>
              )}
            </div>
            <div style={{ fontWeight: 700, fontSize: 20, marginBottom: 16, marginTop: -32, minHeight: 32, display: 'flex', alignItems: 'center', gap: 6 }}>
              {monsterMgmt.nameEditMode ? (
                <>
                  <input
                    type="text"
                    value={monsterMgmt.nameEditValue}
                    autoFocus
                    onChange={e => monsterMgmt.setNameEditValue(e.target.value)}
                    onBlur={monsterMgmt.handleNameEditSave}
                    onKeyDown={e => {
                      if (e.key === 'Enter') monsterMgmt.handleNameEditSave();
                      if (e.key === 'Escape') monsterMgmt.handleNameEditCancel();
                    }}
                    style={{ fontSize: 20, fontWeight: 700, padding: '2px 8px', borderRadius: 6, border: '1.5px solid #1976d2', minWidth: 80 }}
                  />
                  <button onClick={monsterMgmt.handleNameEditSave} style={{ fontSize: 15, marginLeft: 2, color: '#1976d2', background: 'none', border: 'none', cursor: 'pointer' }}>저장</button>
                  <button onClick={monsterMgmt.handleNameEditCancel} style={{ fontSize: 15, color: 'var(--text-main)', background: 'none', border: 'none', cursor: 'pointer' }}>취소</button>
                </>
              ) : (
                <span style={{ cursor: 'pointer' }} title="이름 변경" onClick={monsterMgmt.handleNameEditStart}>{monsterMgmt.managePopup.name || 'NewMonster'}</span>
              )}
            </div>
            {/* 별칭 리스트 및 삭제 */}
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 12, minHeight: 28 }}>
              {monsterMgmt.managePopup && monsterMgmt.managePopup.info[monsterMgmt.managePopup.attribute]?.aliases && monsterMgmt.managePopup.info[monsterMgmt.managePopup.attribute].aliases.length > 0 ? (
                monsterMgmt.managePopup.info[monsterMgmt.managePopup.attribute].aliases.map((a: string) => (
                  <span key={a} style={{ background: '#c7d5ff', color: '#0040ff', borderRadius: 4, padding: '2px 8px', display: 'flex', alignItems: 'center', gap: 2 }}>
                    {a}
                    <span
                      style={{ marginLeft: 2, cursor: 'pointer', color: '#d32f2f', fontWeight: 'bold', fontSize: 15 }}
                      title="삭제"
                      onClick={() => { monsterMgmt.handleAliasDelete(monsterMgmt.managePopup!.name, a, monsterMgmt.managePopup!.attribute); monsterMgmt.setManagePopup((mp: any) => mp && mp.name === monsterMgmt.managePopup!.name ? { ...mp, info: { ...mp.info, [monsterMgmt.managePopup!.attribute]: { ...mp.info[monsterMgmt.managePopup!.attribute], aliases: mp.info[monsterMgmt.managePopup!.attribute].aliases.filter((al: string) => al !== a) } } } : mp); }}
                    >×</span>
                  </span>
                ))
              ) : <span style={{ color: 'var(--text-secondary)' }}>-</span>}
            </div>
            {/* 별칭 추가 */}
            <div style={{ display: 'flex', gap: 0, width: '100%', position: 'relative' }}>
              <input
                type="text"
                value={monsterMgmt.aliasEdit[monsterMgmt.managePopup?.name ?? ''] ?? ''}
                onChange={e => monsterMgmt.setAliasEdit({ ...monsterMgmt.aliasEdit, [monsterMgmt.managePopup?.name ?? '']: e.target.value })}
                style={{ fontSize: 15, padding: '4px 8px', borderRadius: 6, border: '1px solid #bbb', flex: 1, background: 'var(--bg-card)', outline: 'none' }}
                onKeyDown={e => { if (e.key === 'Enter' && monsterMgmt.managePopup) { monsterMgmt.handleAliasAddAll(monsterMgmt.managePopup.name); } }}
              />
              <div style={{ position: 'relative' }}>
                <button
                  ref={aliasAddBtnScale.ref}
                  onMouseEnter={aliasAddBtnScale.scaleUp}
                  onMouseLeave={aliasAddBtnScale.scaleDown}
                  onClick={() => { if (monsterMgmt.managePopup) monsterMgmt.handleAliasAddAll(monsterMgmt.managePopup.name); }}
                  disabled={monsterMgmt.aliasSaving === monsterMgmt.managePopup?.name}
                  style={{ fontSize: 14, background: '#1976d2', color: '#fff', border: 'none', borderRadius: 6, padding: '4px 16px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4 }}
                >
                  추가
                  <span style={{ fontSize: 20, marginLeft: 2, opacity: 0.7, userSelect: 'none', cursor: 'pointer' }}
                    onClick={e => { e.stopPropagation(); monsterMgmt.setShowAliasDropdown(!monsterMgmt.showAliasDropdown); }}
                  >▼</span>
                </button>
                {monsterMgmt.showAliasDropdown && monsterMgmt.managePopup && (
                  <div style={{ position: 'absolute', top: '100%', right: 0, background: '#fff', border: '1px solid #bbb', borderRadius: 6, boxShadow: '0 2px 8px #0002', minWidth: 120, zIndex: 10 }}>
                    <div
                      style={{ padding: '8px 16px', cursor: 'pointer', fontSize: 14, color: 'var(--text-main)', fontWeight: 700, borderRadius: 6, transition: 'background 0.15s' }}
                      onClick={() => {
                        monsterMgmt.setShowAliasDropdown(false);
                        monsterMgmt.handleAliasAdd(monsterMgmt.managePopup!.name);
                      }}
                      onMouseDown={e => e.preventDefault()}
                    >
                      개별 추가
                    </div>
                  </div>
                )}
              </div>
            </div>
            {/* 능력치 (SWARFARM 에서 받아 온 값 — 사람이 고치는 자리가 아니다) */}
            <MonsterStats
              stats={monsterMgmt.managePopup?.info?.[monsterMgmt.managePopup.attribute]?.[monsterMgmt.manageAwakeState]?.stats}
              stars={monsterMgmt.managePopup?.info?.[monsterMgmt.managePopup.attribute]?.[monsterMgmt.manageAwakeState]?.stars}
            />
          </div>
        </div>
      )}
      {/* 콜라보몹 선택 모달 */}
      {monsterMgmt.showCollabSelect && (
        <MonsterSelect
          open={monsterMgmt.showCollabSelect}
          monsterMapping={props.monsterMapping}
          onClose={() => monsterMgmt.setShowCollabSelect(false)}
          onSelect={async (mob) => {
            await monsterMgmt.handleSelectCollab(mob);
            monsterMgmt.setShowCollabSelect(false);
          }}
        />
      )}
    </div>
  );
}

export default Monster; 